<template>
  <div id="app" class="app-safe-area" :class="{ 'has-bottom-nav': showBottomNav }">
    <Header v-if="shouldShowHeader" />
    <TutorialHelpBanner v-if="shouldShowHeader" />
    <router-view></router-view>
    <BottomNavigation />
    <UpdateNotification />
    <NotificationComponent />
    <DebugErrorOverlay />
    <PushDebugOverlay />
    <AppAndroidUpdateModal
      :show="nativeUpdate.visible"
      :mandatory="nativeUpdate.mandatory"
      :message="nativeUpdate.message"
      :store-label="nativeUpdate.storeLabel"
      :store-channel="nativeUpdate.storeChannel"
      :use-app-gallery="nativeUpdate.useAppGallery"
      @update="onNativeUpdateClick"
      @dismiss="onNativeUpdateDismiss"
    />
    <ConfirmacionAppModal />
    <InteractiveHelpHost />
    <!-- Solo para pruebas -->
    <!-- <button @click="testNotification" style="position: fixed; bottom: 20px; right: 20px; z-index: 9999;">
      Test Notification
    </button> -->
  </div>
</template>

<script>
import Header from "@/components/HeaderComponent.vue";
import BottomNavigation from "@/components/BottomNavigation.vue";
import UpdateNotification from "@/components/UpdateNotification.vue";
import { useDarkMode } from "@/composables/useDarkMode";
import { useBottomNavShell } from "@/composables/useBottomNavShell";
import NotificationComponent from "@/components/NotificationComponent.vue";
import DebugErrorOverlay from "@/components/DebugErrorOverlay.vue";
import PushDebugOverlay from "@/components/PushDebugOverlay.vue";
import AppAndroidUpdateModal from "@/components/AppAndroidUpdateModal.vue";
import ConfirmacionAppModal from "@/components/common/ConfirmacionAppModal.vue";
import TutorialHelpBanner from "@/components/TutorialHelpBanner.vue";
import InteractiveHelpHost from "@/components/help/InteractiveHelpHost.vue";
import {
  dismissOptionalUpdate,
  openMicentrumStoreForDevice,
  requestNativeUpdateCheck,
  resolveUpdateChannel,
  startNativeAppUpdateWatcher,
} from "@/services/appVersionService";
import {
  onMounted,
  onUnmounted,
  ref,
  onBeforeUnmount,
} from "vue";
import { wsService } from "@/services/websocketService";
import { useNotificationStore } from "@/store/notificationStore";
import { notificationService } from "@/services/notificationService";
import { useRouter } from "vue-router";
import { authService } from "@/services/authService";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { resolvePushNavigationTarget } from "@/utils/pushNavigation";
import { isMessagingEnabled } from "@/config/messaging";
import { useMessagingStore } from "@/store/messagingStore";
import { useTransportRealtimeStore } from "@/store/transportRealtimeStore";
import { saveMessagingReturnContext } from "@/composables/useMessagingNavigation";
import { clearStaleAuthState } from "@/utils/sessionCacheService";
import { useCursoStore } from "@/store/cursoStore";
import { resolveAuthenticatedResumeRoute } from "@/navigation/bottomNavVisibility";
import { bootstrapActiveCursoFromGuardian } from "@/services/bootstrapActiveCurso";
import { resetIosVisualViewportScale } from "@/utils/resetIosVisualViewportScale";
export default {
  name: "App",
  components: {
    Header,
    BottomNavigation,
    UpdateNotification,
    NotificationComponent,
    DebugErrorOverlay,
    PushDebugOverlay,
    AppAndroidUpdateModal,
    ConfirmacionAppModal,
    TutorialHelpBanner,
    InteractiveHelpHost,
  },
  data() {
    return {
      isAuthenticated: false,
    };
  },
  computed: {
    shouldShowHeader() {
      if (this.$route.meta?.hideHeader) {
        return false;
      }
      // Lista de rutas donde NO debe mostrarse el header
      const authRoutes = [
        "login",
        "register",
        "password-recovery",
        "magic-link",
        "callback",
        "validar-qr",
        "convivencia-asistencia",
      ];
      
      // No mostrar header si estamos en una ruta de autenticación
      if (authRoutes.includes(this.$route.name)) {
        return false;
      }
      
      // Solo mostrar header si el usuario está autenticado
      return this.isAuthenticated;
    },
  },
  created() {
    // Verifica el estado de autenticación al inicio
    this.checkAuthStatus();

    // Inicializar modo oscuro
    this.initializeDarkMode();
  },
  methods: {
    checkAuthStatus() {
      // Usar authService para verificar si el token es válido y no está expirado
      this.isAuthenticated = authService.isAuthenticated();
    },

    initializeDarkMode() {
      useDarkMode().initializeDarkMode();
    },
  },
  watch: {
    // Verificar autenticación cuando cambia la ruta
    $route() {
      this.checkAuthStatus();
    },
  },
  setup() {
    const notificationStore = useNotificationStore();
    const router = useRouter();
    const isAuthenticated = ref(authService.isAuthenticated());
    const messagingStore = useMessagingStore();
    const { showBottomNav } = useBottomNavShell();
    const nativeUpdate = ref({
      visible: false,
      mandatory: false,
      message: "",
      latestVersionCode: undefined,
      storeLabel: "Actualizar en Google Play",
      storeChannel: "play",
      useAppGallery: false,
      updateChannel: undefined,
    });

    let stopNativeUpdateWatcher = () => {};

    const applyNativeUpdateState = (updateState) => {
      nativeUpdate.value = {
        visible: updateState.visible,
        mandatory: updateState.mandatory,
        message: updateState.message,
        latestVersionCode: updateState.latestVersionCode,
        storeLabel: updateState.storeLabel || "Actualizar en Google Play",
        storeChannel: updateState.storeChannel || "play",
        useAppGallery: Boolean(updateState.useAppGallery),
        updateChannel:
          updateState.updateChannel || resolveUpdateChannel() || undefined,
      };
    };

    // Escucha el evento de cambio de visibilidad
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Guarda la ruta deseada cuando la aplicación se cierra
        localStorage.setItem("lastRoute", "/user/main-view");
      }
    };

    onMounted(async () => {
      try {
        console.log("Iniciando servicios...");
        const sessionOk = await authService.ensureValidSession();
        isAuthenticated.value = sessionOk;

        if (sessionOk && localStorage.getItem("userRole") !== "admin") {
          await bootstrapActiveCursoFromGuardian();
        }

        if (isAuthenticated.value && isMessagingEnabled()) {
          await messagingStore.initMessaging();
        useTransportRealtimeStore().init();
        }

        // Cualquier instalación nativa (Play / AppGallery / iOS), no solo login.
        // Re-chequea cada 1 h y al volver a primer plano (debounce 30 min).
        if (Capacitor.isNativePlatform()) {
          stopNativeUpdateWatcher = startNativeAppUpdateWatcher(
            applyNativeUpdateState,
          );
        }

        // Verificar soporte de notificaciones
        if (notificationService.checkSupport()) {
          const permissionGranted =
            await notificationService.requestPermission();
          console.log(
            "Permiso de notificaciones:",
            permissionGranted ? "concedido" : "denegado"
          );
        }

        // Restaurar la última ruta si el usuario está autenticado
        const lastRoute = localStorage.getItem("lastRoute");
        if (
          lastRoute &&
          router.currentRoute.value.name === "login" &&
          isAuthenticated.value
        ) {
          const cursoStore = useCursoStore();
          const resumePath = resolveAuthenticatedResumeRoute(
            lastRoute,
            cursoStore.hasCurso(),
          );
          router.replace({ path: resumePath });
        }
        // // Redirige a la ruta guardada al iniciar la aplicación
        // if (lastRoute) {
        //   router.replace(lastRoute);
        //   localStorage.removeItem("lastRoute");
        // }

        // Guardar la última ruta después de cada navegación
        router.afterEach((to) => {
          if (to.name !== "login") {
            localStorage.setItem("lastRoute", to.fullPath);
          }
          void requestNativeUpdateCheck(false);
        });

        document.addEventListener("visibilitychange", handleVisibilityChange);

        window.addEventListener("microcentrum-push-tap", (event) => {
          if (!authService.isAuthenticated()) return;

          const detail = event?.detail ?? {};
          const target = resolvePushNavigationTarget(detail.data);
          if (!target) return;

          if (target.name) {
            if (target.name === "messaging-conversation") {
              const conversationId = String(detail.data?.conversationId ?? "");
              if (!conversationId) return;
              messagingStore.pushNavIntent = {
                conversationId,
                messageId: detail.data?.messageId ? String(detail.data.messageId) : undefined,
              };
              const curso = (() => {
                try {
                  return JSON.parse(
                    localStorage.getItem("selectedCursoData") || "null",
                  );
                } catch {
                  return null;
                }
              })();
              saveMessagingReturnContext({
                source: "push",
                schoolId: curso?.school_id,
                year: curso?.year,
                courseCode: curso?.course_code || curso?.course_id,
                courseId: curso?.course_id || curso?.course_code,
                returnRouteName: "main-view",
                returnUrl: "/user/main-view",
              });
              router.push({ name: target.name, params: { conversationId } }).catch(() => {});
            } else {
              router.push({ name: target.name }).catch(() => {});
            }
            return;
          }

          if (target.path) {
            router.push(target.path).catch(() => {});
          }
        });

        navigator.serviceWorker?.addEventListener("message", (event) => {
          if (event.data?.type !== "PUSH_NAVIGATION") return;
          window.dispatchEvent(
            new CustomEvent("microcentrum-push-tap", {
              detail: { data: event.data.data },
            })
          );
        });

        // Configurar listener para archivos compartidos en plataformas nativas
        if (Capacitor.isNativePlatform()) {
          const { saveNativeSharePayload, peekNativeSharePayload } =
            await import("@/utils/nativeSharePayload");

          const navigateToSharedFile = (detail) => {
            if (!detail?.cacheFileName && !detail?.uri) {
              return;
            }

            saveNativeSharePayload({
              cacheFileName: detail.cacheFileName,
              fileName: detail.fileName,
              mimeType: detail.mimeType || detail.type,
              uri: detail.uri,
            });

            if (router.currentRoute.value.name === "compartir-archivo") {
              return;
            }

            router.push({
              name: "compartir-archivo",
              query: {
                ...(detail.cacheFileName
                  ? { cacheFile: detail.cacheFileName }
                  : {}),
                ...(detail.fileName ? { fileName: detail.fileName } : {}),
                ...(detail.mimeType || detail.type
                  ? { mimeType: detail.mimeType || detail.type }
                  : {}),
                ...(detail.uri ? { uri: detail.uri } : {}),
                _ts: String(Date.now()),
              },
            });
          };

          window.addEventListener("nativeFileShare", (event) => {
            const detail = event.detail || {};
            console.log("📱 Archivo compartido recibido desde Android:", detail);
            navigateToSharedFile(detail);
          });

          const pendingShare = peekNativeSharePayload();
          if (pendingShare?.cacheFileName || pendingShare?.uri) {
            navigateToSharedFile(pendingShare);
          }

          const { registerDeepLinkListeners } = await import(
            "@/services/deepLinkService"
          );
          await registerDeepLinkListeners(router);

          // Estado de la app en primer plano
          App.addListener("appStateChange", async (state) => {
            if (isMessagingEnabled()) messagingStore.setAppState(state.isActive);
            if (state.isActive) {
              console.log("📱 App volvió al primer plano");
              try {
                resetIosVisualViewportScale({
                  blur: false,
                  onlyIfZoomed: true,
                });
              } catch {
                // Workaround visual; la sesión se valida igual.
              }
              const ok = await authService.ensureValidSession();
              isAuthenticated.value = ok;
              if (!ok) {
                clearStaleAuthState();
                if (router.currentRoute.value?.meta?.requiresAuth) {
                  router.replace({
                    path: "/login",
                    query: { redirect: router.currentRoute.value.fullPath },
                  });
                }
                return;
              }
            }
          });
        }
      } catch (error) {
        console.error("Error en la inicialización:", error);
      }
    });

    onUnmounted(() => {
      if (isMessagingEnabled()) void messagingStore.shutdownMessaging();
      if (wsService.ws) {
        wsService.ws.close();
      }
    });

    onBeforeUnmount(() => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopNativeUpdateWatcher();
    });

    // const handleWebSocketMessage = (event) => {
    //   const data = event.detail;

    //   // Manejar diferentes tipos de mensajes
    //   switch (data.type) {
    //     case "notification":
    //       notificationStore.addNotification({
    //         title: data.title,
    //         message: data.message,
    //       });
    //       break;
    //     // Agregar más casos según necesites
    //   }
    // };

    const testNotification = async () => {
      await notificationStore.addNotification({
        title: "Prueba de Sistema",
        message: "Esta es una notificación de prueba del sistema",
        type: "system",
      });
    };

    const onNativeUpdateClick = async () => {
      await openMicentrumStoreForDevice();
    };

    const onNativeUpdateDismiss = () => {
      const latest = nativeUpdate.value.latestVersionCode;
      const channel = nativeUpdate.value.updateChannel || "play";
      if (latest != null) {
        dismissOptionalUpdate(latest, channel);
      }
      nativeUpdate.value.visible = false;
    };

    return {
      notificationStore,
      testNotification,
      handleVisibilityChange,
      nativeUpdate,
      onNativeUpdateClick,
      onNativeUpdateDismiss,
      showBottomNav,
    };
  },
};
</script>
